import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Shield, Droplets, Zap, Wrench, HardHat, Award,
  BadgeCheck, Building2, MapPin, Phone, Mail,
  Download, FileText, CheckCircle2, ArrowRight, Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import SiteHeader from "@/components/SiteHeader";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PDF_URL = `${BACKEND_URL}/api/static/downloads/sterilemate_capabilities_2026.pdf`;

const CREDENTIALS = [
  { icon: Award, label: "Certified MBE", desc: "Minority Business Enterprise" },
  { icon: Shield, label: "Licensed Medical Device Supplier", desc: "FDA/ISO documented" },
  { icon: Droplets, label: "AAMI ST108 SME", desc: "Sterile water compliance" },
  { icon: HardHat, label: "Healthcare GC", desc: "Water · Electrical · Plumbing" },
  { icon: BadgeCheck, label: "SDVOSB Teaming", desc: "Tier 2 diversity flowdown" },
  { icon: Building2, label: "SAM.gov & HUB Registered", desc: "Public-sector ready" },
];

const AWARDS = [
  "Harris County Hospital System — multiple water purification awards since 2023",
  "State of Texas — water purification & utility contracts at multiple facilities",
  "AAMI ST108 sterile-processing water & dialysis-grade loop deployments",
  "SDVOSB-teamed electrical, plumbing, and construction on active healthcare projects",
];

const ROLES = [
  "Supplier Diversity Officer",
  "Chief Diversity Officer",
  "Procurement / Supply Chain Director",
  "Materials Management",
  "Facilities Director",
  "Chief Financial Officer",
  "Other",
];

export default function DiversityLandingPage() {
  const [form, setForm] = useState({
    name: "", email: "", hospital_name: "", role: "", phone: "", message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.hospital_name) {
      toast.error("Name, email, and hospital are required.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/public/diversity/lead`, {
        ...form,
        source: "diversity_landing",
      });
      setSubmitted(true);
      toast.success("Thanks — capabilities statement download starting.");
      // Trigger download
      window.open(PDF_URL, "_blank", "noopener");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="diversity-landing-page">
      <SiteHeader />

      {/* HERO */}
      <section className="bg-gradient-to-br from-[#1e3a8a] via-[#1e40af] to-[#0c1939] text-white">
        <div className="max-w-6xl mx-auto px-6 py-16 lg:py-20">
          <div className="inline-flex items-center gap-2 bg-amber-400 text-slate-900 text-xs font-black uppercase tracking-widest px-3 py-1.5 rounded-full mb-5" data-testid="diversity-hero-badge">
            <Award className="w-3.5 h-3.5" /> Certified MBE · Healthcare Diversity Partner
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-tight mb-4">
            The MBE that moves the <span className="text-amber-400">categories most diverse vendors can&apos;t.</span>
          </h1>
          <p className="text-lg text-blue-100 max-w-3xl leading-relaxed">
            Clinical consumables. Sterile processing water. General contracting.
            Electrical &amp; plumbing. All under one certified MBE prime — with
            SDVOSB teaming that doubles your diversity line on every invoice.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3 text-sm text-blue-200">
            <span className="inline-flex items-center gap-1.5"><MapPin className="w-4 h-4" /> Houston, TX</span>
            <span className="opacity-40">·</span>
            <span className="inline-flex items-center gap-1.5"><Phone className="w-4 h-4" /> 800.988.3892</span>
            <span className="opacity-40">·</span>
            <a href="mailto:orders@sterilemate.com" className="inline-flex items-center gap-1.5 hover:text-amber-300"><Mail className="w-4 h-4" /> orders@sterilemate.com</a>
          </div>
        </div>
      </section>

      {/* CREDENTIALS STRIP */}
      <section className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 py-10">
          <h2 className="text-base font-bold uppercase tracking-widest text-slate-500 mb-6">Certifications &amp; Capabilities</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {CREDENTIALS.map((c, i) => (
              <div key={i} className="flex items-start gap-3 p-4 bg-slate-50 border border-slate-200 rounded-lg hover:border-amber-400 transition-colors" data-testid={`diversity-cred-${i}`}>
                <div className="shrink-0 w-10 h-10 rounded bg-amber-100 text-amber-800 flex items-center justify-center">
                  <c.icon className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                  <div className="font-bold text-slate-900 text-sm">{c.label}</div>
                  <div className="text-xs text-slate-600 mt-0.5">{c.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MAIN — LEAD CAPTURE + PDF VIEWER */}
      <section className="max-w-6xl mx-auto px-6 py-14">
        <div className="grid lg:grid-cols-5 gap-8">

          {/* LEAD FORM */}
          <div className="lg:col-span-2">
            <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6 lg:sticky lg:top-6">
              {!submitted ? (
                <>
                  <h3 className="text-xl font-black text-slate-900 mb-1">Get our 2026 Capabilities Statement</h3>
                  <p className="text-sm text-slate-600 mb-5">One PDF · MBE cert, NAICS, licenses, awards, contacts.</p>
                  <form onSubmit={submit} className="space-y-3" data-testid="diversity-lead-form">
                    <div>
                      <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Name *</label>
                      <input type="text" required value={form.name} onChange={update("name")} className="w-full mt-1 border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" data-testid="diversity-input-name" />
                    </div>
                    <div>
                      <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Work email *</label>
                      <input type="email" required value={form.email} onChange={update("email")} className="w-full mt-1 border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" data-testid="diversity-input-email" />
                    </div>
                    <div>
                      <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Hospital / Health system *</label>
                      <input type="text" required value={form.hospital_name} onChange={update("hospital_name")} className="w-full mt-1 border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" data-testid="diversity-input-hospital" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Role</label>
                        <select value={form.role} onChange={update("role")} className="w-full mt-1 border border-slate-300 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-400" data-testid="diversity-input-role">
                          <option value="">Select…</option>
                          {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Phone</label>
                        <input type="tel" value={form.phone} onChange={update("phone")} className="w-full mt-1 border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" data-testid="diversity-input-phone" />
                      </div>
                    </div>
                    <div>
                      <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Anything specific we can help with?</label>
                      <textarea rows={3} value={form.message} onChange={update("message")} className="w-full mt-1 border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" data-testid="diversity-input-message" />
                    </div>
                    <Button type="submit" disabled={submitting} className="w-full bg-amber-400 hover:bg-amber-500 text-slate-900 font-black text-sm py-6 shadow-md" data-testid="diversity-submit-btn">
                      {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
                      Download Capabilities Statement
                    </Button>
                    <p className="text-[10px] text-slate-500 text-center pt-1">We&apos;ll add you to our 3-week diversity briefing series. Unsubscribe anytime.</p>
                  </form>
                </>
              ) : (
                <div className="text-center py-8" data-testid="diversity-form-success">
                  <div className="w-14 h-14 bg-emerald-100 text-emerald-700 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle2 className="w-8 h-8" />
                  </div>
                  <h3 className="text-xl font-black text-slate-900 mb-2">Thanks, {form.name.split(" ")[0]}!</h3>
                  <p className="text-sm text-slate-600 mb-4">Your download should have started. If not, click below.</p>
                  <a href={PDF_URL} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-5 py-3 bg-amber-400 hover:bg-amber-500 text-slate-900 rounded-md font-bold text-sm transition-colors" data-testid="diversity-manual-download">
                    <Download className="w-4 h-4" /> Download PDF
                  </a>
                  <p className="text-[11px] text-slate-500 mt-6">We&apos;ll be in touch within one business day.</p>
                </div>
              )}
            </div>
          </div>

          {/* PDF VIEWER */}
          <div className="lg:col-span-3">
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
              <div className="px-5 py-3 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-slate-600" />
                  <span className="text-sm font-bold text-slate-900">2026 Capabilities Statement</span>
                </div>
                <a href={PDF_URL} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-bold text-blue-700 hover:text-blue-900" data-testid="diversity-pdf-inline-download">
                  <Download className="w-3.5 h-3.5" /> Open full
                </a>
              </div>
              <div className="aspect-[8.5/11] bg-slate-100">
                <iframe
                  src={`${PDF_URL}#view=FitH`}
                  title="Sterile Mate 2026 Capabilities Statement"
                  className="w-full h-full"
                  data-testid="diversity-pdf-frame"
                />
              </div>
            </div>

            {/* AWARDS */}
            <div className="mt-6 bg-white border border-slate-200 rounded-xl p-6">
              <h3 className="text-base font-black text-slate-900 mb-3 flex items-center gap-2">
                <Award className="w-4 h-4 text-amber-500" /> Public-sector track record
              </h3>
              <ul className="space-y-2">
                {AWARDS.map((a, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-700" data-testid={`diversity-award-${i}`}>
                    <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                    <span>{a}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* MULTIPLIER CALLOUT */}
      <section className="bg-gradient-to-br from-[#7c2d12] to-[#9a3412] text-white">
        <div className="max-w-6xl mx-auto px-6 py-12 text-center">
          <h2 className="text-2xl sm:text-3xl font-black mb-3">
            One PO. <span className="text-amber-300">MBE + SDVOSB.</span> Double the diversity line.
          </h2>
          <p className="text-orange-100 max-w-2xl mx-auto mb-6">
            Every Sterile Mate invoice earns Tier 1 MBE credit for the full amount
            <em> and</em> generates Tier 2 SDVOSB credit for our veteran-teamed
            subcontract portion. Audit-ready monthly flowdown reports included.
          </p>
          <a href="mailto:orders@sterilemate.com?subject=Add Sterile Mate as an MBE vendor" className="inline-flex items-center gap-2 px-6 py-3 bg-amber-400 hover:bg-amber-500 text-slate-900 rounded-lg font-black text-sm transition-colors" data-testid="diversity-vendor-cta">
            Add Sterile Mate as an MBE vendor <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </section>

      <footer className="bg-[#0c1939] text-slate-400 text-xs py-6">
        <div className="max-w-6xl mx-auto px-6 flex flex-wrap items-center justify-between gap-3">
          <span>Sterile Mate LLC · Certified MBE · Houston, TX</span>
          <span>© 2026 AskIsaac Inc.</span>
        </div>
      </footer>
    </div>
  );
}
